import { Collection, Root, Subsurvey } from "../survey";

function jsonReplace(obj: any, substitutions: {key: string, value: string}[]): any {
  if (!(String.prototype as any).replaceAll) {
    (String.prototype as any).replaceAll = function(str: string, newStr: string){
      // If a regex pattern
      if (Object.prototype.toString.call(str).toLowerCase() === '[object regexp]') {
        return this.replace(str, newStr);
      }
      // If a string
      return this.replace(new RegExp(str, 'g'), newStr);
    };
  }


  if (Array.isArray(obj)) {
    return obj.map((o: any) => jsonReplace(o, substitutions));
  } else if (typeof obj === 'object') {
    return Object.keys(obj).reduce((o: any, k: string) => {
      // Do not replace non-user-input values
      if (['kind','id','_id'].includes(k)) {
        o[k] = obj[k];
        return o;
      }
      o[k] = jsonReplace(obj[k], substitutions);
      return o;
    }, {});
  } else if (typeof obj === 'string'){
    if (substitutions.length) {
      let toReturn = obj as any;
      for (const {key, value} of substitutions) {
        toReturn = toReturn.replaceAll(key, value);
      }
      return toReturn;
    }
  }
  return obj;
}

export function expandGenericTemplates(obj: any): any {
  if (Array.isArray(obj)) {
    const toReturn = obj.map((o: any) => expandGenericTemplates(o));
    return toReturn.reduce((acc, o) => {
      if(!o) {
        return acc;
      }
      else if (o.kind === 'Templated Block') {
        for (const iter of o.iterations) {
          const toInsert = jsonReplace(
            o.components, 
            iter.substitutions.sort((a: any, b: any) => b.key.length - a.key.length) // Sort substitutions to make sure we replace the longest strings first
          );
          acc.push(...toInsert);
        }
      } else {
        acc.push(o);
      }
      return acc;
    }, []);
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).reduce((o: any, k: string) => {
      o[k] = expandGenericTemplates(obj[k]);
      return o;
    }, {});
  }
  return obj;
}

export function addGeneratedSubsurveys<T extends Root>(obj: T): T {
  function processCollection(collection: Collection): Collection {
    // Permissions are based on Collections, so we need to append subsurveys to their nearest parent Collection
    const localSubsurveysToAppend: Subsurvey[] = [];
    let newComponents = collection.components.flatMap((component: any) => {
      return processObjectOrArray(component, localSubsurveysToAppend);
    });

    // Filter subsurveys that don't already exist
    const subsurveysToAdd = localSubsurveysToAppend.filter(subsurvey =>
      !newComponents.some((component: any) => component.kind === 'Subsurvey' && component.path === subsurvey.path)
    );

    return { ...collection, components: [...newComponents, ...subsurveysToAdd] };
  }
  function processObjectOrArray(item: any, subsurveysToAppend: Subsurvey[]): any {
    // Create a subsurvey for each attachment that has allowSendingLinkToUpload set to true
    // these can be shared with applicants via linked subsurveys.
    if (item.kind === 'Attachment' && item.allowSendingLinkToUpload === true) {
      const subsurvey: Subsurvey = {
        kind: 'Subsurvey',
        path: item.targetField + '_automated_collection_form',
        defaultExpiration: {days: 1, weeks: 0, months: 0},
        hideFromNavigation: true,
        sections: [{
          kind: 'Section',
          // TODO: we likely don't want to import all of /i18n for this single use-case,
          // but it would be good to not have to manually maintain these transltions.
          title: {
            'am': 'ሰነድ ስቀል',
            'ar': 'تحميل المستند',
            'bn': 'নথি আপলোড করুন',
            'cni': 'Subir documento',
            'en': 'Upload Document',
            'es': 'Subir documento',
            'fa_AF': 'بارگذاری سند',
            'fa': 'بارگذاری سند',
            'fr': 'Télécharger le document',
            'ha': 'Aika Takardar',
            'hmn': 'Upload ntaub ntawv',
            'ht': 'Telechaje Dokiman',
            'ko': '문서 업로드',
            'mh': 'Upload peba eo',
            'ne': 'डोक्युमेन्ट अपलोड गर्नुहोस्',
            'om': 'Upload Document',
            'pl': 'Prześlij dokument',
            'ps_AF': 'د اسنادو پورته کول',
            'pt': 'Carregar Documento',
            'pua': 'Juchukua Documento',
            'ru': 'Загрузить документ',
            'so': 'Soo rar dukumeenti',
            'tl': 'Mag-upload ng Dokumento',
            'ua': 'Завантажити документ',
            'vi': 'Tải lên tài liệu',
            'yue': '上传文件',
            'zh_CN': '上传文档',
          },
          components: [{
            ...item, // duplicate the attachment component
            allowSendingLinkToUpload: false,
          }]
        }]
      };
      subsurveysToAppend.push(subsurvey);
      return { ...item };
    } else if (item.kind === 'Collection') {
      return processCollection(item);
    } else if (Array.isArray(item)) {
      return item.map(i => processObjectOrArray(i, subsurveysToAppend));
    } else if (item.survey) {
      item.survey = processObjectOrArray(item.survey, subsurveysToAppend);
    } else if (['Section', 'Conditional Block'].includes(item.kind)) {
      item.components = processObjectOrArray(item.components, subsurveysToAppend);
    } else if (['Subsurvey'].includes(item.kind)) {
      item.sections = processObjectOrArray(item.sections, subsurveysToAppend);
    }
    return item;
  }

  const subsurveysToAppend: Subsurvey[] = [];
  const expanded = processObjectOrArray(obj, subsurveysToAppend);

  if (subsurveysToAppend.length) {
    // append the subsurveys to the root of the expanded survey if they aren't in a Collection
    if (Array.isArray(expanded)) {
      return [
        ...expanded,
        ...subsurveysToAppend.filter(subsurvey => !expanded.some((component: any) => component.kind === 'Subsurvey' && component.path === subsurvey.path))
      ] as T;
    } else {
      expanded.survey = [
        ...expanded.survey,
        ...subsurveysToAppend.filter(subsurvey => !expanded.survey.some((component: any) => component.kind === 'Subsurvey' && component.path === subsurvey.path))];
      return expanded;
    }
  }

  return expanded;
}
